import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Network, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Tag, Input, Select, EmptyState, DeadlineBadge, Btn } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";
import { loadStaffBookings, loadRefData } from "@/lib/staffData";
import { deadlineInfo, matchesDeadlineFilter, fmtDate, REQUEST_LABEL, formatReg, INTERNAL_STATUSES } from "@/lib/vda";

const STATUS_LABEL = Object.fromEntries(INTERNAL_STATUSES.map((s) => [s.value, s.label]));
const NEXT_ACTION = {
  new_request: "Place booking", assigned_to_pod: "Submit for booking",
  submitted_for_booking: "Await confirmation", appointment_confirmed: "Await drop-off",
  vehicle_arrived: "Chase garage update", awaiting_internal_authority: "Obtain authority",
  parts_ordered: "Await parts", vehicle_complete: "Arrange collection",
};

async function loadActiveCover() {
  const { data } = await supabase
    .from("booking_cover_assignments")
    .select("id, booking_request_id, staff_user_id, active, staff:user_profiles!staff_user_id(full_name)")
    .eq("active", true);
  return data || [];
}

export default function OtherPods() {
  const nav = useNavigate();
  const { session, profile } = useAuth();
  const uid = session?.user?.id;
  const homePod = profile?.home_pod_id;
  const isAdmin = profile?.role === "administrator";
  const { data, loading, reload } = useAsync(async () => ({
    bookings: await loadStaffBookings(), ref: await loadRefData(), cover: await loadActiveCover(),
  }), []);
  const [q, setQ] = useState("");
  const [podF, setPodF] = useState("");
  const [customer, setCustomer] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(null);

  const coverMap = useMemo(() => {
    const m = {};
    (data?.cover || []).forEach((c) => {
      (m[c.booking_request_id] = m[c.booking_request_id] || []).push(c);
    });
    return m;
  }, [data]);

  const rows = useMemo(() => {
    const all = (data?.bookings || []).filter((b) => b.internal_status !== "completed" && b.internal_status !== "cancelled");
    return all.filter((b) => {
      const pod = b.customers?.pod_id;
      if (!isAdmin && homePod && pod === homePod) return false; // exclude home pod by default
      if (podF && pod !== podF) return false;
      if (customer && b.customer_id !== customer) return false;
      if (!matchesDeadlineFilter(b, due)) return false;
      if (q) {
        const hay = `${b.human_reference} ${b.vehicles?.registration || ""} ${b.customers?.name || ""}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    }).sort((a, b) => {
      const ta = a.next_update_due_at ? new Date(a.next_update_due_at).getTime() : Infinity;
      const tb = b.next_update_due_at ? new Date(b.next_update_due_at).getTime() : Infinity;
      return ta - tb;
    });
  }, [data, homePod, isAdmin, podF, customer, due, q]);

  const join = async (bid) => {
    setBusy(bid);
    try { const r = await api.coverJoin(bid); toast.success(r.status === "already_cover" ? "You're already covering this." : "Joined as cover — it's now in My Work."); reload(); }
    catch (e) { toast.error(e.message || "Could not join as cover"); }
    finally { setBusy(null); }
  };

  if (loading) return <StaffShell><Spinner /></StaffShell>;
  const pods = data.ref.pods || [];
  const podName = (id) => pods.find((p) => p.id === id)?.name || "—";

  return (
    <StaffShell>
      <p className="vda-tick text-[hsl(var(--vda-clay))] flex items-center gap-1.5"><Network className="h-3.5 w-3.5" /> Cross-pod cover</p>
      <h1 className="text-2xl md:text-[30px] font-semibold tracking-tight text-foreground mb-1">Other Pods</h1>
      <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
        Work outside your home pod, for deliberate cover only. Joining as cover adds a booking to your My Work without changing its owning pod, customer or primary AE.
      </p>

      <Panel className="p-3 flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="op-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, registration or customer" className="pl-9" />
        </div>
        <Select data-testid="op-pod" value={podF} onChange={(e) => { setPodF(e.target.value); setCustomer(""); }} className="w-auto min-w-[140px]">
          <option value="">All other pods</option>
          {pods.filter((p) => isAdmin || p.id !== homePod).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select data-testid="op-customer" value={customer} onChange={(e) => setCustomer(e.target.value)} className="w-auto min-w-[160px]">
          <option value="">{podF ? "All customers in pod" : "All customers"}</option>
          {(data.ref.customers || []).filter((c) => !podF || c.pod_id === podF).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Select data-testid="op-due" value={due} onChange={(e) => setDue(e.target.value)} className="w-auto min-w-[130px]">
          <option value="">Any deadline</option>
          <option value="today">Due today</option>
          <option value="due_soon">Due soon</option>
          <option value="overdue">Overdue</option>
          <option value="none">No deadline</option>
        </Select>
      </Panel>

      {rows.length === 0 ? (
        <EmptyState title="Nothing to cover here" hint="No bookings match in other pods right now." />
      ) : (
        <Panel className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[920px]">
              <thead>
                <tr className="bg-secondary text-left vda-tick text-[hsl(var(--vda-quiet))] border-b border-[hsl(var(--vda-strong-border))]">
                  <th className="px-3 py-2.5 font-medium">Pod</th>
                  <th className="px-3 py-2.5 font-medium">Customer</th>
                  <th className="px-3 py-2.5 font-medium">Registration</th>
                  <th className="px-3 py-2.5 font-medium">Reference</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-3 py-2.5 font-medium">Update due</th>
                  <th className="px-3 py-2.5 font-medium">Primary AE</th>
                  <th className="px-3 py-2.5 font-medium">Covering</th>
                  <th className="px-3 py-2.5 font-medium">Next action</th>
                  <th className="px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => {
                  const covers = coverMap[b.id] || [];
                  const iCover = covers.some((c) => c.staff_user_id === uid);
                  return (
                    <tr key={b.id} data-testid={`op-row-${b.human_reference}`} tabIndex={0} role="button"
                      onClick={() => nav(`/staff/booking/${b.id}`)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); nav(`/staff/booking/${b.id}`); } }}
                      className="border-t border-border hover:bg-secondary/40 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                      <td className="px-3 py-2.5"><Tag tone="muted">{podName(b.customers?.pod_id)}</Tag></td>
                      <td className="px-3 py-2.5">{b.customers?.name}</td>
                      <td className="px-3 py-2.5"><span className="vda-reg font-bold">{formatReg(b.vehicles?.registration)}</span></td>
                      <td className="px-3 py-2.5 font-mono text-[11px] text-[hsl(var(--vda-quiet))]">{b.human_reference}</td>
                      <td className="px-3 py-2.5"><Tag tone="muted">{STATUS_LABEL[b.internal_status] || b.public_status}</Tag></td>
                      <td className="px-3 py-2.5"><DeadlineBadge info={deadlineInfo(b.next_update_due_at)} testId={`op-deadline-${b.human_reference}`} /></td>
                      <td className="px-3 py-2.5 text-muted-foreground">{b.assignee?.full_name || <span className="text-[hsl(var(--vda-clay))]">Unassigned</span>}</td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground">{covers.length ? covers.map((c) => c.staff?.full_name).join(", ") : "—"}</td>
                      <td className="px-3 py-2.5 text-xs">{NEXT_ACTION[b.internal_status] || "Review"}</td>
                      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                        {iCover ? <Tag tone="green">Covering</Tag> :
                          <Btn variant="outline" data-testid={`op-join-${b.human_reference}`} onClick={() => join(b.id)} loading={busy === b.id}><UserPlus className="h-3.5 w-3.5" /> Join as cover</Btn>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </StaffShell>
  );
}
